import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { first, map } from 'rxjs/internal/operators';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { OperatorService } from './../../../operator/services/operator.service';
import { PlaceDetailsElectricity } from "../../../account/models/PlaceDetailsElectricity";
import { FinalClientService } from "../../../account/services/final-client.service";
import { CountyDTO } from "../../../shared/models/CountyDTO";
import { CityDTO } from "../../../shared/models/CityDTO";
import { AddressService } from "../../../shared/services/address.service";
import Swal from 'sweetalert2';


@Component({
  selector: 'app-electricity-consumption-place',
  templateUrl: './electricity-consumption-place.component.html',
  styleUrls: ['./electricity-consumption-place.component.scss']
})
export class ElectricityConsumptionPlaceComponent implements OnInit, OnDestroy {

  public form: FormGroup;
  public conventionParts: string[] = [];
  public suppliers: string[] = [];
  private routeDataSub: Subscription;
  public placeId: number;
  public isFormSubmitAttempt = false;
  public placeDetails: PlaceDetailsElectricity;
  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];
  public newPlaceDetails = new PlaceDetailsElectricity();

  @Input()
  public editable = false;
  @Input()
  public isContractChange = false;

  @Input()
  set place(place: any) {
    this.form.patchValue(place);
    this.conventionParts = place.conventionParties;
    this.suppliers = place.suppliers;
  }

  constructor(
    private formBuilder: FormBuilder,
    private finalClientService: FinalClientService,
    private route: ActivatedRoute,
    private router: Router,
    private addressService: AddressService,
    private operatorService: OperatorService
  ) {
    this.createConsumptionPlaceForm();
  }

  createConsumptionPlaceForm(): void {
    this.form = this.formBuilder.group({
      operatorName: [''],
      code: ['', Validators.required],
      placeType: [''],
      isProsumer: ['', Validators.required],
      conventionParties: [''],
      suppliers: [''],
      //  address
      streetNumber: ['', Validators.required],
      streetName: ['', Validators.required],
      postalCode: ['', Validators.required],
      block: [''],
      apartment: [''],
      floor: [''],
      county: ['', Validators.required],
      city: ['', Validators.required],
      //convention
      conventionType: [''],
      conventionNumber: [''],
      conventionDate: [''],
      conventionPresent: [''],
      //technical data
      approvedConsumptionPower: ['', Validators.required],
      voltageLevel: ['', Validators.required],
      smartMeter: ['', Validators.required],
      counterIndexInterval: ['', Validators.required],
      counterIndexDesc: ['', Validators.required],
      counterIndexValue: [''],
      counterIndexReadDate: [''],
      counterSeries: ['', Validators.required],
      counterType: ['', Validators.required],
      connectionType: ['', Validators.required],
      technicalDataStatus: ['', Validators.required],
      networkType: ['', Validators.required],
      operatingMode: ['', Validators.required],
      //network contract
      clientIdentificationNumber: [''],
      clientName: [''],
      contractNumber: [''],
      validFrom: [''],
      validTo: [''],
      contractType: [''],
      //connection certificate
      connectionCertificate: [''],
      connectionCertificateNumber: [''],
      connectionCertificateDate: [''],
      //connection notice
      connectionNotice: [''],
      connectionNoticeNumber: [''],
      connectionNoticeDate: ['']
    });
  }

  getFormData(): void {
    if (this.placeId) {
      this.finalClientService.getFCElectricityConsumptionPlace(this.placeId).pipe(first())
        .subscribe(data => {
          this.form.patchValue(data);
          this.addressService.getCities(this.form.get('county').value).subscribe(dataC => this.cities = dataC);
          if (!this.editable) {
            this.form.disable();
          }
        });
    } else {
      this.operatorService.getOperatorNameConsumptionPlace().pipe(first())
        .subscribe(data => {
          this.form.patchValue(data);
        });
    }
  }

  ngOnInit(): void {
    this.addressService.getCounties().subscribe(data => this.counties = data);
    this.routeDataSub = this.route
      .paramMap
      .pipe(
        map(value => {
          if (window.history.state.editable) {
            this.editable = !!window.history.state.editable;
          }
          this.placeId = +window.history.state.placeId;
          return this.placeId;
        })
      ).subscribe((validationDetails) => this.getFormData());

    if (this.placeId && this.editable) {
      this.form.get('code').disable();
    }
  }

  onSubmit(): void {
    const invalidControls = Object.entries(this.form.controls).filter(([key, control]) => {
      return !!control.invalid;
    });
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      console.log('invalidControls', invalidControls);
      this.validationNotification('Vă rugăm să completați toate câmpurile marcate cu asterix');
    } else {
      this.newPlaceDetails.placeType = 'POD';
      this.newPlaceDetails.operatorName = this.form.get('operatorName').value;
      this.newPlaceDetails.code = this.form.get('code').value;
      this.newPlaceDetails.county = this.form.get('county').value;
      this.newPlaceDetails.city = this.form.get('city').value;
      this.newPlaceDetails.streetName = this.form.get('streetName').value;
      this.newPlaceDetails.streetNumber = this.form.get('streetNumber').value;
      this.newPlaceDetails.block = this.form.get('block').value;
      this.newPlaceDetails.floor = this.form.get('floor').value;
      this.newPlaceDetails.apartment = this.form.get('apartment').value;
      this.newPlaceDetails.postalCode = this.form.get('postalCode').value;
      this.newPlaceDetails.contractNumber = this.form.get('contractNumber').value;
      this.newPlaceDetails.validFrom = this.form.get('validFrom').value;
      this.newPlaceDetails.conventionNumber = this.form.get('conventionNumber').value;
      this.newPlaceDetails.conventionDate = this.form.get('conventionDate').value;
      this.newPlaceDetails.connectionCertificate = this.form.get('connectionCertificate').value;
      this.newPlaceDetails.connectionCertificateNumber = this.form.get('connectionCertificateNumber').value;
      this.newPlaceDetails.connectionCertificateDate = this.form.get('connectionCertificateDate').value;
      this.newPlaceDetails.connectionNotice = this.form.get('connectionNotice').value;
      this.newPlaceDetails.connectionNoticeNumber = this.form.get('connectionNoticeNumber').value;
      this.newPlaceDetails.connectionNoticeDate = this.form.get('connectionNoticeDate').value;
      this.newPlaceDetails.approvedConsumptionPower = this.form.get('approvedConsumptionPower').value;
      this.newPlaceDetails.voltageLevel = this.form.get('voltageLevel').value;
      this.newPlaceDetails.counterIndexDesc = this.form.get('counterIndexDesc').value;
      this.newPlaceDetails.counterIndexValue = this.form.get('counterIndexValue').value;
      this.newPlaceDetails.counterIndexReadDate = this.form.get('counterIndexReadDate').value;
      this.newPlaceDetails.operatingMode = this.form.get('operatingMode').value;
      this.newPlaceDetails.counterIndexInterval = this.form.get('counterIndexInterval').value;
      this.newPlaceDetails.networkType = this.form.get('networkType').value;
      this.newPlaceDetails.smartMeter = this.form.get('smartMeter').value;
      this.newPlaceDetails.counterType = this.form.get('counterType').value;
      this.newPlaceDetails.counterSeries = this.form.get('counterSeries').value;
      this.newPlaceDetails.isProsumer = this.form.get('isProsumer').value;
      this.newPlaceDetails.technicalDataStatus = this.form.get('technicalDataStatus').value;
      this.newPlaceDetails.connectionType = this.form.get('connectionType').value;

      if (this.placeId) {
        Swal.fire({
          title: 'Esti sigur?',
          text: "Esti pe cale sa editezi un loc de consum.",
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Da',
          cancelButtonText: 'Nu',
          customClass: {
            popup: 'popup-z-elevate-24-blue'
          }
        }).then((result) => {
          if (result.isConfirmed) {
            this.newPlaceDetails.id = this.placeId;
            this.operatorService.createElectricityConsumptionPlace(this.newPlaceDetails, "edit").subscribe((val) => {
              this.successEditNotification();
            }, error => {
              if (error) {
                this.errorNotification(error.error.message);
              }
            });
          }
        })
      } else {
        Swal.fire({
          title: 'Esti sigur?',
          text: "Esti pe cale sa adaugi un nou loc de consum.",
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Da',
          cancelButtonText: 'Nu',
          customClass: {
            popup: 'popup-z-elevate-24-blue'
          }
        }).then((result) => {
          this.operatorService.createElectricityConsumptionPlace(this.newPlaceDetails, "create").subscribe((val) => {
            this.router.navigate(['detalii-loc-consum'], {
              relativeTo: this.route,
              state: { placeId: val.id, editable: false }
            });
            this.successCreateNotification();
          }, error => {
            if (error) {
              this.errorNotification(error.error.message);
            }
          });
        })
      }
    }
  }

  onChangeCounty(): void {
    this.cities = [];
    this.addressService.getCities(this.form.get('county').value).subscribe(data => this.cities = data);
  }

  isConventionPresent(): boolean {
    if (this.form.get('conventionPresent').value === null || this.form.get('conventionPresent').value === false) {
      return false;
    } else {
      return true;
    }
  }

  isNetworkTypeDistribution(): boolean {
    if (this.form.get('networkType').value === 'D')
      return true;
    else
      return false;
  }

  isNetworkTypeTransport(): boolean {
    if (this.form.get('networkType').value === 'T')
      return true;
    else
      return false;
  }

  isConsumptionPlaceConnected(): boolean {
    if (this.form.get('technicalDataStatus').value === 'CONECTAT')
      return true;
    else
      return false;
  }

  isConsumptionPlaceDisconnected(): boolean {
    if (this.form.get('technicalDataStatus').value === 'DECONECTAT')
      return true;
    else
      return false;
  }

  isConsumptionPlaceInactive(): boolean {
    if (this.form.get('technicalDataStatus').value === 'INACTIV')
      return true;
    else
      return false;
  }

  isSmartMeter(): boolean {
    if (this.form.get('smartMeter').value === null) {
      return false;
    } else {
      return this.form.get('smartMeter').value;
    }
  }

  isNotSmartMeter(): boolean {
    if (this.form.get('smartMeter').value === null) {
      return false;
    } else {
      return !this.form.get('smartMeter').value;
    }
  }

  isOperatingModeSeasonal(): boolean {
    if (this.form.get('operatingMode').value === 'SEZONIER')
      return true;
    else
      return false;
  }

  isOperatingModePermanent(): boolean {
    if (this.form.get('operatingMode').value === 'PERMANENT')
      return true;
    else
      return false;
  }

  isConnectionTypeMono(): boolean {
    if (this.form.get('connectionType').value === 'MONOFAZAT')
      return true;
    else
      return false;
  }

  isConnectionTypeTri(): boolean {
    if (this.form.get('connectionType').value === 'TRIFAZAT')
      return true;
    else
      return false;
  }

  isProsumer(): boolean {
    if (this.form.get('isProsumer').value === null) {
      return false;
    } else {
      return this.form.get('isProsumer').value;
    }
  }

  isNotProsumer(): boolean {
    if (this.form.get('isProsumer').value === null) {
      return false;
    } else {
      return !this.form.get('isProsumer').value;
    }
  }

  setProsumer(value): void {
    this.form.controls['isProsumer'].setValue(value);
  }

  setSmartMeter(value): void {
    this.form.controls['smartMeter'].setValue(value);
  }

  setTechnicalDataStatus(value): void {
    this.form.controls['technicalDataStatus'].setValue(value);
  }

  setConnectionType(value): void {
    this.form.controls['connectionType'].setValue(value);
  }

  setNetworkType(value): void {
    this.form.controls['networkType'].setValue(value);
  }

  setOperatingMode(value): void {
    this.form.controls['operatingMode'].setValue(value);
  }

  validationNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  errorNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  successCreateNotification(): void {
    Swal.fire({
      title: ' ',
      text: 'Locul de consum ' + this.newPlaceDetails.code + ' a fost creat cu succes',
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  successEditNotification(): void {
    Swal.fire({
      title: ' ',
      text: 'Locul de consum ' + this.newPlaceDetails.code + ' a fost editat cu succes',
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  ngOnDestroy(): void {
    this.routeDataSub.unsubscribe();
  }

}
